import Table from "../../common/components/Table";
import InfohubDocumentStatusBadge, {
  DocumentStatus,
} from "./InfohubDocumentStatusBadge";
import { CheckCircleIcon, XCircleIcon, MinusCircleIcon } from "@heroicons/react/24/outline";

export interface Document {
  id: string;
  fileName: string;
  uploadDate: string;
  uploadedBy: string;
  fileSize: string;
  status: DocumentStatus;
  completionPercentage: number;
  errorMessage?: string;
}

interface InfohubDocumentTableProps {
  documents: Document[];
  isLoading: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onDelete: (documentId: string) => void;
}

function InfohubDocumentTable({
  documents,
  isLoading,
  page,
  totalPages,
  onPageChange,
  onDelete,
}: InfohubDocumentTableProps) {
  const isEmpty = !isLoading && documents.length === 0;

  const getStatusLabel = (status: DocumentStatus): string => {
    const labels: Record<DocumentStatus, string> = {
      PENDING: "준비 중",
      EXTRACTING_TEXT: "텍스트 추출",
      GENERATING_CHUNKS: "청킹",
      GENERATING_EMBEDDINGS: "임베딩 생성",
      COMPLETED: "처리 완료",
      FAILED: "처리 실패",
    };
    return labels[status];
  };

  return (
    <Table.Wrapper className="h-full">
      <Table.Header
        tabs={[
          { label: "파일명", className: "w-[35%]" },
          { label: "업로드 날짜", className: "w-[12%]" },
          { label: "생성자", className: "w-[15%]" },
          { label: "파일 크기", className: "w-[10%]" },
          { label: "처리 상태", className: "w-[23%]" },
          { label: "삭제", className: "w-[5%]" },
        ]}
      />
      <Table.Body
        isLoading={isLoading}
        isEmpty={isEmpty}
        emptyMessage="업로드된 문서가 없습니다."
      >
        {documents.map((document) => (
          <Table.Row key={document.id}>
            <Table.Column className="w-[35%] justify-start">
              <div className="flex items-center gap-2 overflow-hidden">
                {document.status === "COMPLETED" && (
                  <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-green-600" />
                )}
                {document.status === "FAILED" && (
                  <XCircleIcon className="h-5 w-5 flex-shrink-0 text-red-600" />
                )}
                <span className="truncate" title={document.fileName}>
                  {document.fileName}
                </span>
              </div>
            </Table.Column>
            <Table.Column className="w-[12%]">
              <span className="truncate" title={document.uploadDate}>
                {document.uploadDate}
              </span>
            </Table.Column>
            <Table.Column className="w-[15%]">
              <span className="truncate" title={document.uploadedBy}>
                {document.uploadedBy}
              </span>
            </Table.Column>
            <Table.Column className="w-[10%]">
              <span className="truncate" title={document.fileSize}>
                {document.fileSize}
              </span>
            </Table.Column>
            <Table.Column className="w-[23%]">
              <div className="flex w-full flex-col items-center gap-2">
                {document.status === "PENDING" && (
                  <InfohubDocumentStatusBadge status={document.status} />
                )}
                {document.status !== "COMPLETED" && document.status !== "FAILED" && document.status !== "PENDING" && (
                  <div className="w-full px-2">
                    <div className="relative h-2 w-full overflow-hidden rounded-full bg-gray-200">
                      <div
                        className="h-full bg-blue-500 transition-all duration-300"
                        style={{ width: `${document.completionPercentage}%` }}
                      />
                    </div>
                    <div className="mt-1 flex justify-between">
                      <span className="text-xs text-gray-600">
                        {getStatusLabel(document.status)}
                      </span>
                      <span className="text-xs text-gray-600">
                        {document.completionPercentage.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                )}
                {document.status === "COMPLETED" && (
                  <InfohubDocumentStatusBadge status={document.status} />
                )}
                {document.status === "FAILED" && (
                  <>
                    <InfohubDocumentStatusBadge status={document.status} />
                    {document.errorMessage && (
                      <span className="text-xs text-red-600">
                        {document.errorMessage}
                      </span>
                    )}
                  </>
                )}
              </div>
            </Table.Column>
            <Table.Column className="w-[5%]">
              <button
                onClick={() => onDelete(document.id)}
                className="cursor-pointer text-zinc-700 hover:text-red-500"
                title="삭제"
              >
                <MinusCircleIcon className="h-5 w-5" />
              </button>
            </Table.Column>
          </Table.Row>
        ))}
      </Table.Body>
      {!isEmpty && !isLoading && totalPages > 1 && (
        <div className="border border-t-0 border-zinc-300 bg-white py-4">
          <Table.Pagination
            page={page}
            total={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </Table.Wrapper>
  );
}

export default InfohubDocumentTable;
